import { useState, useMemo, useEffect } from "react";
import { Mail, Loader2, ChevronDown, ChevronRight, Download, Archive, FileSpreadsheet, Calendar, X, Truck, Edit3, AlertTriangle, ShoppingBag, Star, FlaskConical, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

var API = "/api";

var STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "shipped", label: "Shipped" },
  { value: "payment_failed", label: "Payment Failed" },
  { value: "refunded", label: "Refunded" },
  { value: "partial_refunded", label: "Partial Refund" },
  { value: "voided", label: "Voided" },
  { value: "cancelled", label: "Cancelled" }
];

function statusColorClass(status) {
  if (status === "completed") return "text-[#8CB369]";
  if (status === "shipped") return "text-[#60a5fa]";
  if (status === "refunded" || status === "partial_refunded") return "text-purple-300";
  if (status === "payment_failed" || status === "voided" || status === "cancelled") return "text-red-300";
  return "text-yellow-400";
}

function isStalePending(order) {
  // A "pending" order older than 30 minutes almost certainly means the customer
  // abandoned the Authorize.Net charge step — the order is a phantom that
  // Authorize never saw. Show a warning so it isn't confused with real sales.
  if ((order.status || "") !== "pending") return false;
  if (!order.created_at) return false;
  try {
    var ageMs = Date.now() - new Date(order.created_at).getTime();
    return ageMs > 30 * 60 * 1000;
  } catch (e) { return false; }
}

function formatPrice(p) {
  return "$" + (p || 0).toFixed(2);
}

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  var d = new Date(dateStr);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
}

function downloadBlob(blob, filename) {
  var url = window.URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(function() { window.URL.revokeObjectURL(url); }, 100);
}

function OrderRow({ order, onResend, sendingId, onInvoice, invoiceId, onStatusChange, onShipClick, savingStatusId, onSendRecovery, recoveryingId, onDelete, deletingId, isSelected, onToggleSelect, viewMode, onRestore, onSendReviewRequest }) {
  var [expanded, setExpanded] = useState(false);
  var sending = sendingId === order.order_id;
  var downloadingInvoice = invoiceId === order.order_id;
  var savingStatus = savingStatusId === order.order_id;
  var statusColor = statusColorClass(order.status);

  return (
    <>
      <tr className="border-b border-white/5 hover:bg-white/5" data-testid={"order-row-" + order.order_id}>
        <td className="p-4 w-10">
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={function(e) { onToggleSelect(order.order_id, e.target.checked); }}
            onClick={function(e) { e.stopPropagation(); }}
            className="accent-[#C0A062] w-4 h-4 cursor-pointer"
            data-testid={"order-select-" + order.order_id}
          />
        </td>
        <td className="p-4">
          <button onClick={function() { setExpanded(!expanded); }} className="text-[#C0A062] font-mono inline-flex items-center gap-2 hover:underline" data-testid={"order-expand-" + order.order_id}>
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {order.order_id}
          </button>
          {isStalePending(order) && (
            <span
              className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-500/10 border border-red-500/40 rounded-sm text-[10px] font-mono uppercase tracking-wider text-red-300 align-middle"
              title="This order was created but the customer never completed the payment step. Authorize.Net never received this transaction. Safe to delete or leave as-is."
              data-testid={"stale-pending-badge-" + order.order_id}
            >
              <AlertTriangle className="w-3 h-3" /> Abandoned
            </span>
          )}
        </td>
        <td className="p-4">
          <p className="text-white">{order.user_name}</p>
          <p className="text-[#71717a] text-sm">{order.user_email}</p>
        </td>
        <td className="p-4 text-[#A1A1AA]">{order.items?.length || 0} items</td>
        <td className="p-4 text-white font-heading font-bold">{formatPrice(order.total)}</td>
        <td className="p-4">
          <div className="flex items-center gap-2">
            <select
              value={order.status || "pending"}
              onChange={function(e) { onStatusChange(order.order_id, e.target.value); }}
              disabled={savingStatus}
              className={"bg-[#0a0a0a] border border-white/10 rounded-sm px-2 py-1 text-xs font-mono uppercase focus:outline-none focus:border-[#C0A062]/60 disabled:opacity-50 " + statusColor}
              data-testid={"status-select-" + order.order_id}
              title="Manually change order status"
            >
              {STATUS_OPTIONS.map(function(o) {
                return <option key={o.value} value={o.value} className="bg-[#0a0a0a] text-white">{o.label}</option>;
              })}
            </select>
            {savingStatus && <Loader2 className="w-3 h-3 animate-spin text-[#C0A062]" />}
          </div>
        </td>
        <td className="p-4 text-[#71717a] text-sm">{formatDate(order.created_at)}</td>
        <td className="p-4 text-right">
          <div className="inline-flex items-center gap-2 flex-wrap justify-end">
            <button
              onClick={function() { onShipClick(order); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded-sm text-xs font-mono uppercase text-[#A1A1AA] hover:text-[#60a5fa] hover:border-[#60a5fa]/40 transition-colors"
              data-testid={"ship-order-" + order.order_id}
              title={order.tracking_number ? "Update tracking" : "Mark as shipped + add tracking"}
            >
              <Truck className="w-3 h-3" />
              {order.tracking_number ? "Edit Ship" : "Ship"}
            </button>
            <button
              onClick={function() { onInvoice(order.order_id); }}
              disabled={downloadingInvoice}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded-sm text-xs font-mono uppercase text-[#A1A1AA] hover:text-[#C0A062] hover:border-[#C0A062]/40 disabled:opacity-50 transition-colors"
              data-testid={"download-invoice-" + order.order_id}
              title="Download PDF invoice"
            >
              {downloadingInvoice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
              {downloadingInvoice ? "..." : "Invoice"}
            </button>
            <button
              onClick={function() { onResend(order.order_id); }}
              disabled={sending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/10 rounded-sm text-xs font-mono uppercase text-[#A1A1AA] hover:text-[#C0A062] hover:border-[#C0A062]/40 disabled:opacity-50 transition-colors"
              data-testid={"resend-receipt-" + order.order_id}
              title="Resend order confirmation email to customer"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              {sending ? "..." : "Resend"}
            </button>
            {(["pending", "cancelled", "payment_failed"].indexOf(order.status) !== -1) && (
              <button
                onClick={function() { onSendRecovery(order); }}
                disabled={recoveryingId === order.order_id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#8CB369]/30 rounded-sm text-xs font-mono uppercase text-[#8CB369] hover:text-[#a3c97a] hover:border-[#8CB369]/60 hover:bg-[#8CB369]/5 disabled:opacity-50 transition-colors"
                data-testid={"send-recovery-" + order.order_id}
                title="Send the customer a 'complete your order' email. Reactivates cancelled/abandoned orders so the resume-cart link works."
              >
                {recoveryingId === order.order_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingBag className="w-3 h-3" />}
                {recoveryingId === order.order_id ? "..." : "Recover"}
              </button>
            )}
            <button
              onClick={function() { onDelete(order); }}
              disabled={deletingId === order.order_id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-500/30 rounded-sm text-xs font-mono uppercase text-red-300 hover:text-red-200 hover:border-red-500/60 hover:bg-red-500/5 disabled:opacity-50 transition-colors"
              data-testid={"delete-order-" + order.order_id}
              title={viewMode === "recycle-bin" ? "Permanently delete this order. Cannot be undone." : "Move this order to the Recycle Bin. Restorable within 30 days."}
            >
              {deletingId === order.order_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              {viewMode === "recycle-bin" ? "Delete Forever" : "Bin"}
            </button>
            {viewMode === "recycle-bin" && (
              <button
                onClick={function() { onRestore(order); }}
                disabled={deletingId === order.order_id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#8CB369]/30 rounded-sm text-xs font-mono uppercase text-[#8CB369] hover:text-[#a3c97a] hover:border-[#8CB369]/60 hover:bg-[#8CB369]/5 disabled:opacity-50 transition-colors"
                data-testid={"restore-order-" + order.order_id}
                title="Restore from Recycle Bin"
              >
                <ShoppingBag className="w-3 h-3" /> Restore
              </button>
            )}
            {viewMode === "active" && (
              <button
                onClick={function() { onSendReviewRequest(order); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#C0A062]/30 rounded-sm text-xs font-mono uppercase text-[#C0A062] hover:bg-[#C0A062]/5 hover:border-[#C0A062]/60 transition-colors"
                data-testid={"send-review-" + order.order_id}
                title="Send a review-request email to this customer right now (bypasses the 14-day eligibility check)."
              >
                <Star className="w-3 h-3" /> Review
              </button>
            )}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-[#0f0f0f]" data-testid={"order-details-" + order.order_id}>
          <td colSpan="8" className="p-4 border-b border-white/5">
            <div className="space-y-3">
              <div>
                <p className="font-mono text-xs uppercase text-[#71717a] mb-2">Items</p>
                <div className="space-y-1">
                  {(order.items || []).map(function(item, idx) {
                    return (
                      <div key={idx}>
                        <div className="flex justify-between text-sm">
                          <span className="text-white">{item.quantity}× {item.name}</span>
                          <span className="text-[#A1A1AA]">{formatPrice(item.price * item.quantity)}</span>
                        </div>
                        {(item.addons || []).map(function(addon, ai) {
                          var addonQty = (addon.quantity || 1) * (item.quantity || 1);
                          return (
                            <div key={ai} className="flex justify-between text-xs text-[#71717a] pl-6">
                              <span>↳ {addonQty}× {addon.name}</span>
                              <span>{formatPrice(addon.price * addonQty)}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-white/5">
                <div>
                  <p className="font-mono text-xs uppercase text-[#71717a]">Subtotal</p>
                  <p className="text-white">{formatPrice(order.subtotal)}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-[#71717a]">Shipping</p>
                  <p className="text-white">{formatPrice(order.shipping)}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-[#71717a]">Total</p>
                  <p className="text-[#C0A062] font-bold">{formatPrice(order.total)}</p>
                </div>
              </div>
              {order.shipping_address && (
                <div className="pt-3 border-t border-white/5">
                  <p className="font-mono text-xs uppercase text-[#71717a] mb-1">Ship to</p>
                  <p className="text-sm text-white">
                    {order.shipping_address.address}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
                  </p>
                </div>
              )}
              {order.tracking_number && (
                <div className="pt-3 border-t border-white/5" data-testid={"tracking-info-" + order.order_id}>
                  <p className="font-mono text-xs uppercase text-[#71717a] mb-1">Shipping</p>
                  <p className="text-sm text-white">
                    <span className="text-[#60a5fa]">{order.tracking_carrier || "USPS"}</span> · <span className="font-mono">{order.tracking_number}</span>
                    {order.shipped_at && <span className="text-[#71717a] text-xs ml-2">shipped {formatDate(order.shipped_at)}</span>}
                  </p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function OrdersTab({ orders, onRefresh }) {
  var [sendingId, setSendingId] = useState(null);
  var [savingStatusId, setSavingStatusId] = useState(null);
  var [shipOrder, setShipOrder] = useState(null);
  var [trackingNumber, setTrackingNumber] = useState("");
  var [carrier, setCarrier] = useState("USPS");
  var [notifyCustomer, setNotifyCustomer] = useState(true);
  var [shipSubmitting, setShipSubmitting] = useState(false);
  var [invoiceId, setInvoiceId] = useState(null);
  var [bulkDownloading, setBulkDownloading] = useState(false);
  var [csvDownloading, setCsvDownloading] = useState(false);
  var [cleaningAbandoned, setCleaningAbandoned] = useState(false);
  var [sendingReviewBackfill, setSendingReviewBackfill] = useState(false);
  var [recoveryingId, setRecoveryingId] = useState(null);
  var [deletingId, setDeletingId] = useState(null);
  var [selectedIds, setSelectedIds] = useState({});
  var [bulkDeleting, setBulkDeleting] = useState(false);
  var [viewMode, setViewMode] = useState("active");  // 'active' | 'recycle-bin'
  var [archivedOrders, setArchivedOrders] = useState([]);
  var [sendingReviewBulk, setSendingReviewBulk] = useState(false);

  // Fetch archived orders when entering the recycle-bin view
  useEffect(function() {
    if (viewMode !== "recycle-bin") return;
    axios.get(API + "/orders?archived=true", { withCredentials: true })
      .then(function(res) { setArchivedOrders(res.data || []); })
      .catch(function() { setArchivedOrders([]); });
  }, [viewMode]);

  function refreshAll() {
    if (typeof onRefresh === "function") onRefresh();
    if (viewMode === "recycle-bin") {
      axios.get(API + "/orders?archived=true", { withCredentials: true })
        .then(function(res) { setArchivedOrders(res.data || []); })
        .catch(function() {});
    }
  }

  // Source list depending on viewMode
  var sourceOrders = viewMode === "recycle-bin" ? archivedOrders : orders;

  // Selected order IDs that are currently visible (intersection with filteredOrders)
  function selectedCount() {
    return Object.keys(selectedIds).filter(function(k) { return selectedIds[k]; }).length;
  }
  function handleToggleSelect(orderId, checked) {
    setSelectedIds(function(prev) {
      var next = Object.assign({}, prev);
      if (checked) next[orderId] = true;
      else delete next[orderId];
      return next;
    });
  }
  function handleToggleSelectAll(checked) {
    if (checked) {
      var next = Object.assign({}, selectedIds);
      filteredOrders.forEach(function(o) { next[o.order_id] = true; });
      setSelectedIds(next);
    } else {
      // Deselect only currently-visible ones
      var visibleIds = new Set(filteredOrders.map(function(o) { return o.order_id; }));
      var next = {};
      Object.keys(selectedIds).forEach(function(k) {
        if (!visibleIds.has(k)) next[k] = selectedIds[k];
      });
      setSelectedIds(next);
    }
  }
  function handleBulkDelete() {
    var ids = Object.keys(selectedIds).filter(function(k) { return selectedIds[k]; });
    if (ids.length === 0) return;
    var inBin = viewMode === "recycle-bin";
    var msg = inBin
      ? "PERMANENTLY DELETE " + ids.length + " order(s)? This cannot be undone."
      : "Move " + ids.length + " order(s) to the Recycle Bin?\n\nYou can restore them within 30 days.";
    if (!window.confirm(msg)) return;
    setBulkDeleting(true);
    var url = inBin ? "/orders/bulk-permanent-delete" : "/orders/bulk-delete";
    var verb = inBin ? "Permanently deleted" : "Moved to Recycle Bin";
    axios.post(API + url, { order_ids: ids }, { withCredentials: true })
      .then(function(res) {
        var n = (res.data && (res.data.deleted || res.data.archived)) || 0;
        toast.success(verb + ": " + n + " order(s)");
        setSelectedIds({});
        refreshAll();
      })
      .catch(function(err) {
        var m = (err && err.response && err.response.data && err.response.data.detail) || "Bulk delete failed";
        toast.error(m);
      })
      .finally(function() { setBulkDeleting(false); });
  }

  function handleBulkRestore() {
    var ids = Object.keys(selectedIds).filter(function(k) { return selectedIds[k]; });
    if (ids.length === 0) return;
    if (!window.confirm("Restore " + ids.length + " order(s) from the Recycle Bin?")) return;
    setBulkDeleting(true);
    axios.post(API + "/orders/bulk-restore", { order_ids: ids }, { withCredentials: true })
      .then(function(res) {
        toast.success("Restored " + ((res.data && res.data.restored) || 0) + " order(s)");
        setSelectedIds({});
        refreshAll();
      })
      .catch(function(err) {
        var m = (err && err.response && err.response.data && err.response.data.detail) || "Restore failed";
        toast.error(m);
      })
      .finally(function() { setBulkDeleting(false); });
  }
  var [startDate, setStartDate] = useState("");
  var [endDate, setEndDate] = useState("");
  var [searchText, setSearchText] = useState("");
  var [statusFilter, setStatusFilter] = useState("all");
  var [hideTestOrders, setHideTestOrders] = useState(true);
  var [excludedEmails, setExcludedEmails] = useState([]);

  // Fetch the canonical excluded-emails list from the backend on mount.
  // Kept in sync with the same set the review-request system uses.
  useEffect(function() {
    axios.get(API + "/excluded-emails", { withCredentials: true })
      .then(function(res) {
        var list = (res.data && res.data.excluded_emails) || [];
        setExcludedEmails(list.map(function(e) { return (e || "").toLowerCase(); }));
      })
      .catch(function() { /* non-critical; toggle just won't filter anything */ });
  }, []);

  // Filter orders by date range, search text, and status for the visible table.
  var filteredOrders = useMemo(function() {
    var startMs = startDate ? new Date(startDate + "T00:00:00").getTime() : null;
    var endMs = endDate ? new Date(endDate + "T23:59:59.999").getTime() : null;
    var q = (searchText || "").trim().toLowerCase();
    var excludedSet = new Set(excludedEmails);
    return sourceOrders.filter(function(o) {
      if (hideTestOrders && o.user_email && excludedSet.has(o.user_email.toLowerCase())) return false;
      if (o.created_at) {
        var t = new Date(o.created_at).getTime();
        if (startMs && t < startMs) return false;
        if (endMs && t > endMs) return false;
      }
      if (statusFilter !== "all" && (o.status || "") !== statusFilter) return false;
      if (q) {
        var hay = [
          o.order_id, o.user_email, o.user_name, o.payment_id, o.anet_transaction_id
        ].filter(Boolean).join(" ").toLowerCase();
        if (hay.indexOf(q) === -1) return false;
      }
      return true;
    });
  }, [sourceOrders, startDate, endDate, searchText, statusFilter, hideTestOrders, excludedEmails]);

  // Header-checkbox state: are all/some currently-visible rows selected?
  var allVisibleSelected = filteredOrders.length > 0 && filteredOrders.every(function(o) { return !!selectedIds[o.order_id]; });
  var someVisibleSelected = filteredOrders.some(function(o) { return !!selectedIds[o.order_id]; });
  var selectedCountNum = Object.keys(selectedIds).filter(function(k) { return selectedIds[k]; }).length;
  // Count of test orders matching the exclusion list (used in the toggle label)
  var testOrderCount = 0;
  for (var i = 0; i < sourceOrders.length; i++) {
    var em = sourceOrders[i].user_email;
    if (em && excludedEmails.indexOf(em.toLowerCase()) !== -1) testOrderCount++;
  }

  function buildDateQuery() {
    var qs = [];
    if (startDate) qs.push("start=" + encodeURIComponent(startDate));
    if (endDate) qs.push("end=" + encodeURIComponent(endDate));
    return qs.length > 0 ? "?" + qs.join("&") : "";
  }

  function clearDates() {
    setStartDate("");
    setEndDate("");
    setSearchText("");
    setStatusFilter("all");
  }

  function setQuickRange(rangeKey) {
    var now = new Date();
    var y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    function iso(date) {
      return date.toISOString().slice(0, 10);
    }
    if (rangeKey === "30d") {
      var thirtyAgo = new Date(y, m, d - 30);
      setStartDate(iso(thirtyAgo)); setEndDate(iso(now));
    } else if (rangeKey === "thisMonth") {
      setStartDate(iso(new Date(y, m, 1))); setEndDate(iso(now));
    } else if (rangeKey === "lastMonth") {
      setStartDate(iso(new Date(y, m - 1, 1)));
      setEndDate(iso(new Date(y, m, 0)));
    } else if (rangeKey === "thisYear") {
      setStartDate(iso(new Date(y, 0, 1))); setEndDate(iso(now));
    } else if (rangeKey === "lastYear") {
      setStartDate(iso(new Date(y - 1, 0, 1)));
      setEndDate(iso(new Date(y - 1, 11, 31)));
    }
  }

  function handleResend(orderId) {
    setSendingId(orderId);
    axios.post(API + "/orders/" + orderId + "/resend-receipt", {}, { withCredentials: true })
      .then(function(res) {
        toast.success("Receipt resent to " + res.data.to);
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || "Failed to resend receipt";
        toast.error(msg);
      })
      .finally(function() { setSendingId(null); });
  }

  function handleSendRecovery(order) {
    var name = (order.user_name || "the customer");
    var msg = "Send the abandoned-cart recovery email to " + name + " (" + (order.user_email || "no email") + ")?";
    if (order.status !== "pending") {
      msg += "\n\nThis will REACTIVATE the order (status: " + order.status + " → pending) and reset its 24-hour cleanup clock so the customer's resume-cart link works.";
    }
    if (!window.confirm(msg)) return;
    setRecoveryingId(order.order_id);
    axios.post(API + "/orders/" + order.order_id + "/resend-recovery-email", {}, { withCredentials: true })
      .then(function(res) {
        toast.success(res.data.message || "Recovery email sent");
        if (onRefresh) onRefresh();
      })
      .catch(function(err) {
        var m = (err && err.response && err.response.data && err.response.data.detail) || "Failed to send recovery email";
        toast.error(m);
      })
      .finally(function() { setRecoveryingId(null); });
  }

  function handleDeleteOrder(order) {
    var name = (order.user_name || "this customer");
    var inBin = viewMode === "recycle-bin";
    var msg;
    if (inBin) {
      msg = "PERMANENTLY DELETE order " + order.order_id + "?\n\nCustomer: " + name + " (" + (order.user_email || "no email") + ")\nStatus: " + (order.status || "unknown") + "\n\nThis cannot be undone.";
    } else {
      msg = "Move order " + order.order_id + " to the Recycle Bin?\n\nCustomer: " + name + " (" + (order.user_email || "no email") + ")\nStatus: " + (order.status || "unknown") + "\nTotal: $" + (Number(order.total) || 0).toFixed(2) + "\n\nYou can restore it from Admin → Recycle Bin within 30 days.";
    }
    if (!window.confirm(msg)) return;
    setDeletingId(order.order_id);
    var req = inBin
      ? axios.delete(API + "/orders/" + order.order_id + "/permanent", { withCredentials: true })
      : axios.delete(API + "/orders/" + order.order_id, { withCredentials: true });
    req.then(function() {
        toast.success(inBin ? "Permanently deleted " + order.order_id : "Moved " + order.order_id + " to Recycle Bin");
        refreshAll();
      })
      .catch(function(err) {
        var m = (err && err.response && err.response.data && err.response.data.detail) || "Failed";
        toast.error(m);
      })
      .finally(function() { setDeletingId(null); });
  }

  function handleRestoreOrder(order) {
    if (!window.confirm("Restore order " + order.order_id + " from the Recycle Bin?")) return;
    setDeletingId(order.order_id);
    axios.post(API + "/orders/" + order.order_id + "/restore", {}, { withCredentials: true })
      .then(function() { toast.success("Restored " + order.order_id); refreshAll(); })
      .catch(function(err) {
        var m = (err && err.response && err.response.data && err.response.data.detail) || "Restore failed";
        toast.error(m);
      })
      .finally(function() { setDeletingId(null); });
  }

  function handleSendReviewRequestSingle(order) {
    var name = (order.user_name || "the customer");
    if (!window.confirm("Send a review-request email to " + name + " (" + (order.user_email || "no email") + ")?\n\nThis bypasses the 14-day age check and the prior-email-sent check. Use it to manually request reviews from selected customers.")) return;
    setSendingReviewBulk(true);
    axios.post(API + "/orders/" + order.order_id + "/send-review-request", {}, { withCredentials: true })
      .then(function(res) { toast.success("Review request sent to " + res.data.to); })
      .catch(function(err) {
        var m = (err && err.response && err.response.data && err.response.data.detail) || "Failed to send";
        toast.error(m);
      })
      .finally(function() { setSendingReviewBulk(false); });
  }

  function handleSendReviewRequestBulk() {
    var ids = Object.keys(selectedIds).filter(function(k) { return selectedIds[k]; });
    if (ids.length === 0) return;
    if (!window.confirm("Send a review-request email to " + ids.length + " selected order(s)?\n\nThis bypasses eligibility checks. Test/excluded emails are still skipped.")) return;
    setSendingReviewBulk(true);
    axios.post(API + "/orders/bulk-send-review-request", { order_ids: ids }, { withCredentials: true })
      .then(function(res) {
        var d = res.data;
        toast.success("Sent " + (d.sent || 0) + " · skipped " + ((d.skipped_excluded || 0) + (d.skipped_no_email || 0)) + " · errors " + (d.errors || 0));
        setSelectedIds({});
      })
      .catch(function(err) {
        var m = (err && err.response && err.response.data && err.response.data.detail) || "Bulk send failed";
        toast.error(m);
      })
      .finally(function() { setSendingReviewBulk(false); });
  }

  function handleStatusChange(orderId, newStatus) {
    setSavingStatusId(orderId);
    axios.patch(API + "/orders/" + orderId + "/status", { status: newStatus }, { withCredentials: true })
      .then(function() {
        toast.success("Status updated to '" + newStatus + "'");
        if (onRefresh) onRefresh();
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || "Failed to update status";
        toast.error(msg);
      })
      .finally(function() { setSavingStatusId(null); });
  }

  function handleShipClick(order) {
    setShipOrder(order);
    setTrackingNumber(order.tracking_number || "");
    setCarrier(order.tracking_carrier || "USPS");
    setNotifyCustomer(!order.tracking_number);  // notify by default only on first-time ship
  }

  function closeShipModal() {
    setShipOrder(null);
    setTrackingNumber("");
    setCarrier("USPS");
    setNotifyCustomer(true);
    setShipSubmitting(false);
  }

  function submitShip() {
    if (!shipOrder) return;
    if (!trackingNumber.trim()) { toast.error("Tracking number is required"); return; }
    setShipSubmitting(true);
    axios.post(API + "/orders/" + shipOrder.order_id + "/ship", {
      tracking_number: trackingNumber.trim(),
      carrier: carrier.trim() || "USPS",
      notify_customer: notifyCustomer
    }, { withCredentials: true })
      .then(function(res) {
        toast.success("Order marked as shipped" + (res.data.email_sent ? " · customer notified" : ""));
        closeShipModal();
        if (onRefresh) onRefresh();
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || "Failed to update shipping";
        toast.error(msg);
        setShipSubmitting(false);
      });
  }

  function handleInvoice(orderId) {
    setInvoiceId(orderId);
    axios.get(API + "/orders/" + orderId + "/invoice", {
      responseType: "blob",
      withCredentials: true
    })
      .then(function(res) {
        downloadBlob(res.data, "RGP-Invoice-" + orderId + ".pdf");
        toast.success("Invoice downloaded");
      })
      .catch(function() {
        toast.error("Failed to download invoice");
      })
      .finally(function() { setInvoiceId(null); });
  }

  function handleBulkDownload() {
    setBulkDownloading(true);
    axios.get(API + "/orders/invoices/download-all" + buildDateQuery(), {
      responseType: "blob",
      withCredentials: true
    })
      .then(function(res) {
        var label = startDate || endDate ? (startDate || "all") + "_to_" + (endDate || "now") : "All";
        downloadBlob(res.data, "RGP-Invoices-" + label + ".zip");
        toast.success("Invoices downloaded as ZIP");
      })
      .catch(function(err) {
        var msg = "Failed to download invoices";
        if (err && err.response && err.response.status === 404) {
          msg = "No orders in this date range";
        }
        toast.error(msg);
      })
      .finally(function() { setBulkDownloading(false); });
  }

  function handleCsvExport() {
    setCsvDownloading(true);
    axios.get(API + "/orders/export.csv" + buildDateQuery(), {
      responseType: "blob",
      withCredentials: true
    })
      .then(function(res) {
        var label = startDate || endDate ? (startDate || "all") + "_to_" + (endDate || "now") : "All";
        downloadBlob(res.data, "RGP-Orders-" + label + ".csv");
        toast.success("Orders exported to CSV");
      })
      .catch(function() {
        toast.error("Failed to export CSV");
      })
      .finally(function() { setCsvDownloading(false); });
  }

  function handleCleanAbandoned() {
    if (!window.confirm("PERMANENTLY DELETE every abandoned order?\n\nThis covers (a) pending orders older than 24h and (b) any previously-cancelled orders flagged abandoned. These are checkouts where the customer never completed payment — no cards were charged, Authorize.Net never saw them. A summary email is sent to admin@resurrectedgunparts.com.\n\nThis cannot be undone.")) return;
    setCleaningAbandoned(true);
    axios.post(API + "/cleanup-abandoned-orders", {}, { withCredentials: true })
      .then(function(res) {
        var n = res.data && res.data.count;
        var v = res.data && res.data.total_value;
        if (n > 0) {
          toast.success("Cleaned " + n + " abandoned order(s) ($" + (v || 0).toFixed(2) + ")");
        } else {
          toast.success("Nothing to clean — no pending orders older than 24h.");
        }
        if (typeof onRefresh === "function") onRefresh();
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || "Cleanup failed";
        toast.error(msg);
      })
      .finally(function() { setCleaningAbandoned(false); });
  }

  function handleSendReviewBackfill() {
    if (!window.confirm("Send review-request emails to ALL past customers who haven't received one yet?\n\n• Customers get a single-use 10% off code (60-day expiry)\n• Test orders and your own emails are excluded\n• Each customer is emailed at most once\n• Going forward, this fires automatically 14 days after each new purchase")) return;
    setSendingReviewBackfill(true);
    axios.post(API + "/send-review-requests", {}, { withCredentials: true })
      .then(function(res) {
        var n = (res.data && res.data.sent) || 0;
        var errs = (res.data && res.data.errors) || 0;
        if (n > 0) {
          toast.success("Sent " + n + " review request(s)" + (errs ? " (" + errs + " failed)" : ""));
        } else {
          toast.success("No eligible orders to email right now. New orders will be emailed automatically 14d after placement.");
        }
        if (typeof onRefresh === "function") onRefresh();
      })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || "Failed to send review requests";
        toast.error(msg);
      })
      .finally(function() { setSendingReviewBackfill(false); });
  }

  var hasFilter = !!(startDate || endDate || searchText.trim() || statusFilter !== "all");
  var totalLabel = hasFilter
    ? filteredOrders.length + " of " + orders.length + " orders match"
    : orders.length + " total orders";

  return (
    <div className="space-y-4">
      {/* Search + status + date filter */}
      <div className="bg-[#121212] border border-white/10 rounded-sm p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px] space-y-1">
            <Label className="text-[10px] text-[#71717a] uppercase">Search</Label>
            <Input
              type="text"
              value={searchText}
              onChange={function(e) { setSearchText(e.target.value); }}
              placeholder="Email, name, order #, or transaction ID..."
              className="input-tactical h-9"
              data-testid="orders-search-input"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-[#71717a] uppercase">Status</Label>
            <select
              value={statusFilter}
              onChange={function(e) { setStatusFilter(e.target.value); }}
              className="input-tactical h-9 w-44"
              data-testid="orders-status-filter"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="shipped">Shipped</option>
              <option value="payment_failed">Payment failed</option>
              <option value="refunded">Refunded</option>
              <option value="partial_refunded">Partial refund</option>
              <option value="voided">Voided</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-[#71717a] uppercase">Test Orders</Label>
            <label
              className="flex items-center gap-2 h-9 px-3 border border-white/10 rounded-sm cursor-pointer hover:border-[#C0A062]/40 transition-colors"
              data-testid="hide-test-orders-toggle"
              title="Test orders = orders placed by your excluded internal/test email addresses. Hidden by default to declutter the view."
            >
              <input
                type="checkbox"
                checked={hideTestOrders}
                onChange={function(e) { setHideTestOrders(e.target.checked); }}
                className="accent-[#C0A062]"
              />
              <FlaskConical className="w-3 h-3 text-[#71717a]" />
              <span className="text-[#A1A1AA] text-xs font-mono uppercase whitespace-nowrap">
                Hide test{testOrderCount > 0 ? " (" + testOrderCount + ")" : ""}
              </span>
            </label>
          </div>
        </div>

        <div className="border-t border-white/5 pt-3">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-[#C0A062]" />
            <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider">Filter by Date</Label>
          </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] text-[#71717a] uppercase">Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={function(e) { setStartDate(e.target.value); }}
              max={endDate || undefined}
              className="input-tactical h-9 w-44"
              data-testid="filter-start-date"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-[#71717a] uppercase">End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={function(e) { setEndDate(e.target.value); }}
              min={startDate || undefined}
              className="input-tactical h-9 w-44"
              data-testid="filter-end-date"
            />
          </div>
          {hasFilter && (
            <button
              onClick={clearDates}
              className="inline-flex items-center gap-1 px-3 h-9 text-xs font-mono uppercase text-[#A1A1AA] hover:text-white border border-white/10 rounded-sm"
              data-testid="filter-clear"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <span className="text-[10px] text-[#71717a] font-mono uppercase self-center mr-1">Quick:</span>
          {[
            { key: "30d", label: "Last 30 days" },
            { key: "thisMonth", label: "This Month" },
            { key: "lastMonth", label: "Last Month" },
            { key: "thisYear", label: "This Year" },
            { key: "lastYear", label: "Last Year" }
          ].map(function(r) {
            return (
              <button
                key={r.key}
                onClick={function() { setQuickRange(r.key); }}
                className="px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-[#A1A1AA] hover:text-[#C0A062] border border-white/10 hover:border-[#C0A062]/40 rounded-sm transition-colors"
                data-testid={"filter-quick-" + r.key}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <p className="text-[#A1A1AA] text-sm">{totalLabel}</p>
          <div className="inline-flex border border-white/10 rounded-sm overflow-hidden" data-testid="view-mode-toggle">
            <button
              onClick={function() { setViewMode("active"); setSelectedIds({}); }}
              className={"px-3 py-1.5 text-xs font-mono uppercase " + (viewMode === "active" ? "bg-[#C0A062] text-black" : "bg-transparent text-[#A1A1AA] hover:text-white")}
              data-testid="view-active-btn"
            >
              Active
            </button>
            <button
              onClick={function() { setViewMode("recycle-bin"); setSelectedIds({}); }}
              className={"px-3 py-1.5 text-xs font-mono uppercase " + (viewMode === "recycle-bin" ? "bg-[#C0A062] text-black" : "bg-transparent text-[#A1A1AA] hover:text-white")}
              data-testid="view-bin-btn"
              title="Orders in the Recycle Bin are auto-purged after 30 days."
            >
              🗑 Recycle Bin
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedCountNum > 0 && viewMode === "recycle-bin" && (
            <button
              onClick={handleBulkRestore}
              disabled={bulkDeleting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#8CB369]/10 border border-[#8CB369]/60 rounded-sm text-xs font-mono uppercase text-[#8CB369] hover:bg-[#8CB369]/20 disabled:opacity-50 transition-colors"
              data-testid="bulk-restore-btn"
            >
              {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingBag className="w-3 h-3" />}
              Restore Selected ({selectedCountNum})
            </button>
          )}
          {selectedCountNum > 0 && viewMode === "active" && (
            <button
              onClick={handleSendReviewRequestBulk}
              disabled={sendingReviewBulk}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#C0A062]/10 border border-[#C0A062]/60 rounded-sm text-xs font-mono uppercase text-[#C0A062] hover:bg-[#C0A062]/20 disabled:opacity-50 transition-colors"
              data-testid="bulk-review-btn"
              title="Send review-request emails to selected orders. Bypasses 14-day age check."
            >
              {sendingReviewBulk ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
              Send Review to Selected ({selectedCountNum})
            </button>
          )}
          {selectedCountNum > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/60 rounded-sm text-xs font-mono uppercase text-red-200 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
              data-testid="bulk-delete-btn"
              title={viewMode === "recycle-bin" ? "Permanently delete selected orders" : "Move selected orders to the Recycle Bin"}
            >
              {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              {viewMode === "recycle-bin" ? "Delete Forever" : "Move to Bin"} ({selectedCountNum})
            </button>
          )}
          <button
            onClick={handleCleanAbandoned}
            disabled={cleaningAbandoned}
            className="inline-flex items-center gap-2 px-4 py-2 border border-red-500/30 rounded-sm text-xs font-mono uppercase text-red-300 hover:text-red-200 hover:border-red-500/60 hover:bg-red-500/5 disabled:opacity-50 transition-colors"
            data-testid="clean-abandoned-btn"
            title="Permanently delete every abandoned order (pending >24h or previously flagged abandoned). Cannot be undone."
          >
            {cleaningAbandoned ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
            Delete Abandoned
          </button>
          <button
            onClick={handleSendReviewBackfill}
            disabled={sendingReviewBackfill}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#C0A062]/40 rounded-sm text-xs font-mono uppercase text-[#C0A062] hover:bg-[#C0A062]/5 hover:border-[#C0A062]/70 disabled:opacity-50 transition-colors"
            data-testid="send-review-backfill-btn"
            title="One-time backfill: email all eligible past customers a review request + 10% off coupon. Going forward, this fires automatically 14 days after each new order."
          >
            {sendingReviewBackfill ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
            Review Backfill
          </button>
          <button
            onClick={handleCsvExport}
            disabled={csvDownloading || filteredOrders.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 border border-white/10 rounded-sm text-xs font-mono uppercase text-[#A1A1AA] hover:text-white hover:border-white/30 disabled:opacity-50 transition-colors"
            data-testid="export-orders-csv"
            title={hasFilter ? "Export filtered orders as CSV" : "Export all orders as CSV"}
          >
            {csvDownloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}
            {csvDownloading ? "Building CSV..." : (hasFilter ? "Export CSV (Filtered)" : "Export CSV")}
          </button>
          <button
            onClick={handleBulkDownload}
            disabled={bulkDownloading || filteredOrders.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#C0A062]/40 rounded-sm text-xs font-mono uppercase text-[#C0A062] hover:bg-[#C0A062]/10 disabled:opacity-50 transition-colors"
            data-testid="download-all-invoices"
            title={hasFilter ? "Download filtered invoices as ZIP" : "Download all invoices as ZIP"}
          >
            {bulkDownloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
            {bulkDownloading ? "Building ZIP..." : (hasFilter ? "Download Invoices ZIP (Filtered)" : "Download All Invoices (ZIP)")}
          </button>
        </div>
      </div>

      <div className="bg-[#121212] border border-white/5 rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 bg-[#0a0a0a]">
              <th className="text-left p-4 w-10">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={function(el) { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                  onChange={function(e) { handleToggleSelectAll(e.target.checked); }}
                  className="accent-[#C0A062] w-4 h-4 cursor-pointer"
                  data-testid="orders-select-all"
                  title="Select / deselect all visible orders"
                />
              </th>
              <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Order ID</th>
              <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Customer</th>
              <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Items</th>
              <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Total</th>
              <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Status</th>
              <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Date</th>
              <th className="text-right p-4 font-mono text-xs text-[#A1A1AA] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map(function(order) {
              return <OrderRow key={order.order_id} order={order} onResend={handleResend} sendingId={sendingId} onInvoice={handleInvoice} invoiceId={invoiceId} onStatusChange={handleStatusChange} onShipClick={handleShipClick} savingStatusId={savingStatusId} onSendRecovery={handleSendRecovery} recoveryingId={recoveryingId} onDelete={handleDeleteOrder} deletingId={deletingId} isSelected={!!selectedIds[order.order_id]} onToggleSelect={handleToggleSelect} viewMode={viewMode} onRestore={handleRestoreOrder} onSendReviewRequest={handleSendReviewRequestSingle} />;
            })}
            {filteredOrders.length === 0 && (
              <tr><td colSpan="8" className="p-8 text-center text-[#71717a]">
                {hasFilter ? "No orders in this date range" : "No orders yet"}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Ship / Tracking modal */}
      {shipOrder && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center px-4" data-testid="ship-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-sm max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-bold text-white text-lg uppercase flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#60a5fa]" />
                {shipOrder.tracking_number ? "Edit Shipping" : "Mark as Shipped"}
              </h3>
              <button onClick={closeShipModal} className="text-[#71717a] hover:text-white" data-testid="close-ship-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-1 mb-4 text-sm">
              <p className="text-[#71717a]">Order</p>
              <p className="text-[#C0A062] font-mono">{shipOrder.order_id}</p>
              <p className="text-white">{shipOrder.user_name} <span className="text-[#71717a]">· {shipOrder.user_email}</span></p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Carrier</Label>
                <select
                  value={carrier}
                  onChange={function(e) { setCarrier(e.target.value); }}
                  className="input-tactical w-full"
                  data-testid="ship-carrier-select"
                >
                  <option value="USPS">USPS</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                  <option value="DHL">DHL</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <Label className="text-[#A1A1AA] text-xs uppercase mb-1 block">Tracking Number *</Label>
                <Input
                  value={trackingNumber}
                  onChange={function(e) { setTrackingNumber(e.target.value); }}
                  placeholder="Paste tracking number..."
                  className="input-tactical font-mono"
                  data-testid="ship-tracking-input"
                  autoFocus
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyCustomer}
                  onChange={function(e) { setNotifyCustomer(e.target.checked); }}
                  data-testid="ship-notify-toggle"
                />
                <span className="text-sm text-white">Email the customer their tracking link</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-white/5">
              <button
                onClick={closeShipModal}
                className="px-4 py-2 text-sm font-mono uppercase border border-white/10 rounded-sm text-[#A1A1AA] hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={submitShip}
                disabled={shipSubmitting || !trackingNumber.trim()}
                className="px-4 py-2 text-sm font-mono uppercase bg-[#C0A062] text-black rounded-sm hover:bg-[#d4b675] disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="ship-submit-btn"
              >
                {shipSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                {shipSubmitting ? "Saving..." : (shipOrder.tracking_number ? "Update" : "Mark Shipped")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
